import React, { useEffect, useState } from 'react';
import { Button, Image, Skeleton, TextInput } from '@mantine/core';
import PropTypes from 'prop-types';
import { AlertCircle, Photo } from 'tabler-icons-react';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import AppCard from '../../common/AppCard';
import AppFlexbox from '../../common/AppFlexbox';
import PaginationList from '../../common/PaginationList';
import * as classes from '../../../styles/nestedStyles.module.css';
import AppCheckbox from '../../common/AppCheckbox';

const StoreProductsSelect = ({
  loading,
  products,
  selectedProductIds,
  onSelectProducts
}) => {
  const [filterState, setFilterState] = useState({
    index: 1,
    search: ''
  });

  const filteredProducts = products.filter(
    (t) =>
      !filterState.search ||
      t.name.toLowerCase().includes(filterState.search.toLowerCase())
  );

  const allProductsSelected = filteredProducts.every((p) =>
    selectedProductIds.includes(p.value)
  );

  useEffect(() => {
    setFilterState({
      index: 1,
      search: ''
    });
  }, []);

  return (
    <AppStack style={{ gap: 10 }}>
      <TextInput
        disabled={loading || products.length === 0}
        onChange={(e) =>
          setFilterState({
            ...filterState,
            search: e.currentTarget.value,
            pageIndex: 1
          })
        }
        placeholder="Search products by name"
        size="sm"
        style={{ marginTop: 5 }}
        value={filterState.search}
      />

      <AppFlexbox
        style={{
          gap: 8,
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <AppText style={{ fontSize: 14, fontWeight: 500 }}>
          Available products
        </AppText>
        {filteredProducts.length > 0 && (
          <AppCheckbox
            checked={allProductsSelected}
            label="Select all"
            onChange={() => {
              onSelectProducts(
                allProductsSelected
                  ? selectedProductIds.filter(
                      (id) => !filteredProducts.some((p) => p.value === id)
                    )
                  : [
                      ...selectedProductIds,
                      ...filteredProducts

                        .map((p) => p.value)
                        .filter((id) => !selectedProductIds.includes(id))
                    ]
              );
            }}
          />
        )}
      </AppFlexbox>

      <AppStack
        style={{
          flex: 1,
          borderRadius: 5,
          height: '100%',
          overflow: 'hidden'
        }}
      >
        {loading ? (
          <AppStack style={{ gap: 2, border: 'solid 1px lightgrey' }}>
            {Array(2)
              .fill()
              .map((_, s) => (
                <AppCard
                  // eslint-disable-next-line react/no-array-index-key
                  key={s}
                  radius={8}
                  shadow="sm"
                  style={{ padding: 0 }}
                  withBorder
                >
                  <AppFlexbox
                    style={{
                      minHeight: 40,
                      flex: 1,
                      padding: 12,
                      alignItems: 'center'
                    }}
                  >
                    <Skeleton height={30} radius={30} width={30} />
                    <AppStack style={{ flex: 1, gap: 2 }}>
                      <Skeleton height={10} mb={6} width="50%" />
                      <Skeleton height={10} width="40%" />
                    </AppStack>
                  </AppFlexbox>
                </AppCard>
              ))}
          </AppStack>
        ) : filteredProducts.length === 0 ? (
          <AppStack
            style={{
              padding: 20,
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              border: 'solid 1px lightgrey',
              gap: 5
            }}
          >
            <AlertCircle color="#000" size={30} />
            <AppText style={{ fontSize: 16, color: '#000' }}>
              No products found
            </AppText>

            {filterState.search && (
              <Button
                color="dark"
                onClick={() => {
                  setFilterState({
                    ...filterState,
                    search: '',
                    pageIndex: 1
                  });
                }}
                variant="outline"
              >
                Clear search
              </Button>
            )}
          </AppStack>
        ) : (
          <PaginationList
            items={filteredProducts
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((p) => {
                const isSelected = selectedProductIds.includes(p.value);

                return (
                  <AppFlexbox
                    key={p.value}
                    className={classes['hover-action-card']}
                    onClick={() => {
                      onSelectProducts(
                        isSelected
                          ? [
                              ...selectedProductIds.filter(
                                (id) => id !== p.value
                              )
                            ]
                          : [...selectedProductIds, p.value]
                      );
                    }}
                    style={{
                      padding: '10px 16px',
                      gap: 16,
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <AppCheckbox checked={isSelected} onChange={() => {}} />
                    <AppStack
                      style={{
                        width: 40,
                        height: 40,
                        border: p.previewImage ? 'none' : 'solid 1px #dee2e6',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {p.previewImage ? (
                        <Image h={40} src={p.previewImage} w={40} />
                      ) : (
                        <Photo color="#666" size={24} />
                      )}
                    </AppStack>
                    <AppStack style={{ gap: 3 }}>
                      <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                        {p.name}
                      </AppText>
                    </AppStack>
                  </AppFlexbox>
                );
              })}
            itemsPerPage={10}
            onPageChange={(pageIndex) =>
              setFilterState({
                ...filterState,
                pageIndex
              })
            }
            pageIndex={filterState.pageIndex}
          />
        )}
      </AppStack>
    </AppStack>
  );
};

StoreProductsSelect.propTypes = {
  onSelectProducts: PropTypes.func.isRequired,
  products: PropTypes.array.isRequired,
  selectedProductIds: PropTypes.array.isRequired,
  loading: PropTypes.bool
};

export default StoreProductsSelect;
